import { Link } from 'react-router-dom';

import { DEFAULT_PATH, LOGIN_PATH } from '../app/screens';
import { useBrand } from '../theme/useBrand';

import { LandingPreview } from './LandingPreview';
import './landing.css';

/** The three propositions of `Docsight Landing.dc.html` § "WHAT YOU GET". */
const PROPOSITIONS = [
  {
    id: 'see',
    tone: 'accent',
    title: 'See everything',
    body: 'Volume, type, location, scanner and source — live, filterable, and drillable down to the single page. Every number traces to a query you can inspect.',
    icon: (
      <svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1.5" />
        <rect x="9" y="1.5" width="5.5" height="5.5" rx="1.5" />
        <rect x="1.5" y="9" width="5.5" height="5.5" rx="1.5" />
        <rect x="9" y="9" width="5.5" height="5.5" rx="1.5" />
      </svg>
    ),
  },
  {
    id: 'teach',
    tone: 'ok',
    title: 'Teach it in minutes',
    body: 'Uncertain documents queue for keyboard-first review. Create a category once and a sweep re-classifies every lookalike from stored OCR — no re-scanning.',
    icon: (
      <svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <circle cx="8" cy="8" r="6.5" />
        <polyline points="5.5 8 7.3 9.8 10.8 6.3" />
      </svg>
    ),
  },
  {
    id: 'ask',
    tone: 'violet',
    title: 'Ask in plain English',
    body: '“Which branch scans the most invoices?” The chatbot answers from your data only — with the chart, the query behind it, and a one-click jump into the dashboard.',
    icon: (
      <svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <rect x="1.5" y="2.5" width="13" height="9.5" rx="2.5" />
        <path d="M5 12 L5 14.5 L8 12" />
        <circle cx="5.2" cy="7.2" r="0.8" fill="currentColor" stroke="none" />
        <circle cx="8" cy="7.2" r="0.8" fill="currentColor" stroke="none" />
        <circle cx="10.8" cy="7.2" r="0.8" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
] as const;

/** The pipeline as the landing page tells it — ingest → OCR → classify → insight. */
const STEPS = [
  {
    step: '01 · INGEST',
    title: 'Any source, one API',
    body: 'Scanners, apps, webhooks or AI agents post to a single endpoint. Deduped, virus-scanned, never lost.',
  },
  {
    step: '02 · OCR',
    title: 'Read every page',
    body: 'Text and layout extracted asynchronously — workers scale with your backlog, quiet hours cost nothing.',
  },
  {
    step: '03 · CLASSIFY',
    title: 'Named & explained',
    body: 'Each document gets a category, a confidence score and a written rationale — against your taxonomy.',
  },
  {
    step: '04 · INSIGHT',
    title: 'Dashboards & answers',
    body: 'Pre-aggregated analytics in milliseconds, ranked automation opportunities, and a chatbot on top.',
  },
] as const;

/** Sample white-label tenants: one platform, three brands, zero shared data. */
const WHITE_LABEL_TENANTS = [
  { initial: 'A', colour: '#1f6feb', name: 'Acme DocInsight', hostname: 'acme.abcdash.co.uk' },
  { initial: 'B', colour: '#5ba648', name: 'Borealis Docs', hostname: 'docs.borealis.health' },
  { initial: 'K', colour: '#2d2ba6', name: 'Keystone Papers', hostname: 'papers.keystone.legal' },
] as const;

/** The compliance claims the partner panel makes, ticked. */
const PARTNER_ASSURANCES = [
  'UK Azure, GDPR-ready',
  'Per-tenant retention & erasure',
  'Tenant isolation enforced in the data layer',
  'WCAG 2.1 AA',
] as const;

/** The Askelie + ABC lockup, repeated in the hero byline and the footer. */
function AttributionMarks(): React.JSX.Element {
  return (
    <span className="landing-marks" aria-hidden="true">
      <img className="landing-marks-img" src="/brand/askelie.png" alt="" />
      <img className="landing-marks-img landing-marks-img--overlap" src="/brand/abc.jpeg" alt="" />
    </span>
  );
}

/**
 * The public landing page (`planning/UI design/Docsight Landing.dc.html`): the
 * only marketing surface of the product and the entry point a signed-out visitor
 * reaches at `/`. It sells the four-stage pipeline and the white-label story, and
 * hands off to `/login` (Sign in) or `/dashboard` (which the auth guard sends to
 * sign-in when there is no session).
 *
 * It is branded like the sign-in card: the product name and accent come from the
 * hostname's branding profile, so a partner's own domain never says "Docsight".
 */
export function LandingScreen(): React.JSX.Element {
  const { brand, logoUrl } = useBrand();
  const brandName = brand.product_name;
  const initial = brandName.charAt(0).toUpperCase();

  return (
    <div className="landing">
      <header className="landing-header">
        {logoUrl != null ? (
          <img className="landing-mark landing-mark--logo" src={logoUrl} alt="" aria-hidden="true" />
        ) : (
          <span className="landing-mark" aria-hidden="true">
            {initial}
          </span>
        )}
        <span className="landing-brand">{brandName}</span>
        <nav className="landing-nav" aria-label="Sections">
          <a href="#product">Product</a>
          <a href="#how">How it works</a>
          <a href="#partners">For partners</a>
        </nav>
        <Link className="landing-signin" to={LOGIN_PATH}>
          Sign in
        </Link>
      </header>

      <main className="landing-main">
        <section className="landing-hero" aria-labelledby="landing-hero-title">
          <div>
            <p className="landing-pill">
              <span className="landing-pill-dot" aria-hidden="true" />
              Document intelligence &amp; analytics platform
            </p>
            <h1 className="landing-h1" id="landing-hero-title">
              Know what your organisation is scanning.
            </h1>
            <p className="landing-lede">
              Every scanned document, ingested, OCR&apos;d and auto-classified — with a live dashboard, a
              human review queue that teaches the system, ranked automation opportunities, and a chatbot
              that answers in plain English.
            </p>
            <p className="landing-cta-row">
              <Link className="landing-cta" to={DEFAULT_PATH}>
                Open the dashboard
              </Link>
              <a className="landing-cta landing-cta--ghost" href="#how">
                See how it works
              </a>
            </p>
            <p className="landing-byline">
              <AttributionMarks />
              Built by Askelie · delivered by ABC Managed Solutions · white-label for your tenants
            </p>
          </div>
          <LandingPreview brandInitial={initial} />
        </section>

        <section className="landing-section" id="product" aria-labelledby="landing-product">
          <h2 className="landing-eyebrow" id="landing-product">
            What you get
          </h2>
          <div className="landing-grid landing-grid--3">
            {PROPOSITIONS.map((proposition) => (
              <article className="landing-card" key={proposition.id}>
                <span className={`landing-icon landing-icon--${proposition.tone}`} aria-hidden="true">
                  {proposition.icon}
                </span>
                <h3 className="landing-card-title">{proposition.title}</h3>
                <p className="landing-card-body">{proposition.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-section" id="how" aria-labelledby="landing-how">
          <h2 className="landing-eyebrow" id="landing-how">
            How it works
          </h2>
          <ol className="landing-grid landing-grid--4 landing-steps">
            {STEPS.map((step) => (
              <li className="landing-card landing-card--step" key={step.step}>
                <span className="landing-step-index mono">{step.step}</span>
                <h3 className="landing-step-title">{step.title}</h3>
                <p className="landing-card-body">{step.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="landing-section" id="partners" aria-labelledby="landing-partners">
          <div className="landing-panel">
            <div>
              <h2 className="landing-eyebrow landing-eyebrow--on-dark" id="landing-partners">
                For partners &amp; resellers
              </h2>
              <p className="landing-panel-lede">
                Your brand on the front. Your tenants, isolated. One console.
              </p>
              <p className="landing-panel-body">
                {brandName} is white-label by design: name, logo and colours resolve per hostname, light
                and dark from the same tokens. Onboard a tenant in minutes, watch volumes and review
                backlogs across your whole book, and step into any tenant with a fully audited context
                switch.
              </p>
              <ul className="landing-assurances">
                {PARTNER_ASSURANCES.map((assurance) => (
                  <li key={assurance}>
                    <span aria-hidden="true">✓</span> {assurance}
                  </li>
                ))}
              </ul>
            </div>
            <div className="landing-tenants">
              {WHITE_LABEL_TENANTS.map((tenant) => (
                <p className="landing-tenant" key={tenant.hostname}>
                  <span
                    className="landing-tenant-mark"
                    style={{ background: tenant.colour }}
                    aria-hidden="true"
                  >
                    {tenant.initial}
                  </span>
                  <span className="landing-tenant-name">{tenant.name}</span>
                  <span className="landing-tenant-host mono">{tenant.hostname}</span>
                </p>
              ))}
              <p className="landing-tenants-note">one platform · three brands · zero shared data</p>
            </div>
          </div>
        </section>

        <section className="landing-close" aria-labelledby="landing-close-title">
          <h2 className="landing-close-title" id="landing-close-title">
            Turn scanned paper into answers.
          </h2>
          <p className="landing-close-body">See the live dashboard with sample data — no setup needed.</p>
          <p>
            <Link className="landing-cta" to={DEFAULT_PATH}>
              Open the dashboard
            </Link>
          </p>
        </section>
      </main>

      <footer className="landing-footer">
        <span className="landing-mark landing-mark--sm" aria-hidden="true">
          {initial}
        </span>
        <span className="landing-footer-note">
          {brandName} — a white-label product name, swapped per tenant
        </span>
        <span className="landing-footer-credit">
          <AttributionMarks />© 2026 Askelie · ABC Managed Solutions
        </span>
      </footer>
    </div>
  );
}
